import React from 'react';


/*
 * Progress during the test
 *
 * Three deliberate choices here, all aimed at the first twenty questions, which
 * is where the database says almost all abandonment happens.
 *
 * 1. Time, not a count. "Question 1 / 65" states a quota; "about 5 minutes left"
 *    states a duration, and people forgive durations far more readily. The
 *    per question figures below are measured from the answers table, not guessed.
 *
 * 2. Stages. A single goal 65 questions away exerts no pull at question 3. Five
 *    goals thirteen apart mean the next one is always close. Stages are purely
 *    visual: crossing one must never interrupt, because 83% of finishers take the
 *    first exit they are offered, and four stage-end prompts would be four new
 *    exits.
 *
 * 3. A head start. The landing page already collected the test type, the script
 *    and an email, so the bar credits that instead of opening at a bare zero.
 *    People finish what looks already started.
 */

const NUMBER_OF_STAGES = 5;

/* Credit for the choices already made on the landing page */
const SETUP_CREDIT = 0.08;

/* Median seconds per question among people who finish. Vocabulary is measured on
   production, the other two on the local mirror, and all are rounded up so that
   most people beat the estimate rather than miss it. */
export const SECONDS_PER_QUESTION = {
	vocab: 5.0,
	hanzi: 7.5,
	chengyu: 5.5
};


/*
 * The credible interval, shown as a width and never as a value.
 *
 * "Your estimate is within plus or minus 300 words" tells you how much is still
 * unknown without telling you the answer, so unlike a provisional result it
 * cannot be collected by stopping early. It gives the reader something that
 * visibly improves with every question, which a bare counter never did.
 *
 * The grid of candidate counts is coarse at the top end, so the raw number is
 * rounded rather than presented as though it were exact.
 */
export function halfWidth(interval) {
	if(!interval || interval.length !== 2) return null;
	const width = (Number(interval[1]) - Number(interval[0])) / 2;
	if(isNaN(width) || width <= 0) return null;
	if(width < 200) return Math.max(Math.round(width/10)*10, 10);
	return Math.round(width/50)*50;
}


/*
 * How fast this particular reader is going.
 *
 * A fast reader should see their own faster number rather than the site median,
 * so the median is treated as a prior and the reader's own pace is blended in as
 * evidence accumulates.
 *
 * But the result is capped at the median, so the estimate can only ever go down.
 * A slow reader being told they have twelve minutes left where the median says
 * five is being told they are behind, and discouraging them is exactly the
 * abandonment this whole change exists to prevent. The cost is that a slow
 * reader gets an optimistic number, which the word "about" is doing the work of
 * covering.
 *
 * The observed pace is also clamped from below, because elapsed time counts
 * minutes spent with the tab in the background.
 */
const PRIOR_WEIGHT = 8;

function pacePerQuestion(answered, timestampStartTest, medianSeconds) {
	if(!timestampStartTest || answered < 1) return medianSeconds;

	const elapsed = (Date.now() / 1000) - timestampStartTest;
	if(!(elapsed > 0)) return medianSeconds;

	let observed = elapsed / answered;
	const floor = 0.4 * medianSeconds;
	if(observed < floor) observed = floor;

	const blended = (PRIOR_WEIGHT * medianSeconds + answered * observed) / (PRIOR_WEIGHT + answered);

	/* Never slower than the median: the estimate may shorten, never lengthen */
	return Math.min(blended, medianSeconds);
}


/*
 * Rounding to whole minutes left the final twelve questions, nearly a fifth of
 * the test, showing one unchanging line. Since the question counter is gone
 * there was nothing else moving except the stage bar, and that is exactly the
 * stretch where momentum matters most.
 *
 * The last minute is therefore split in two. Seconds were the alternative, but a
 * ticking countdown reads like a timed exam, which is the opposite of the point.
 */
function minutesLeft(numberOfQuestions, maxNumberOfQuestions, secondsPerQuestion) {
	const questionsLeft = Math.max(maxNumberOfQuestions - numberOfQuestions + 1, 0);
	const secondsLeft = questionsLeft * secondsPerQuestion;

	if(secondsLeft < 20) return 'Last few questions';
	if(secondsLeft < 60) return 'Under a minute left';

	const minutes = Math.ceil(secondsLeft / 60);
	if(minutes === 1) return 'About a minute left';
	return 'About ' + minutes + ' minutes left';
}


class ProgressBar extends React.Component {
	render() {
		const numberOfQuestions = this.props.numberOfQuestions;
		const maxNumberOfQuestions = this.props.maxNumberOfQuestions;
		const medianSeconds = this.props.secondsPerQuestion || SECONDS_PER_QUESTION.vocab;

		const answered = Math.max(numberOfQuestions - 1, 0);
		const secondsPerQuestion = pacePerQuestion(answered, this.props.timestampStartTest, medianSeconds);
		const stageSize = Math.ceil(maxNumberOfQuestions / NUMBER_OF_STAGES);
		const currentStage = Math.min(Math.floor(answered / stageSize) + 1, NUMBER_OF_STAGES);

		/* The bar never starts empty: the setup already done counts for something */
		const rawProgress = maxNumberOfQuestions > 0 ? answered / maxNumberOfQuestions : 0;
		const progressInPercents = 100 * (SETUP_CREDIT + (1 - SETUP_CREDIT) * rawProgress);

		const stages = [];
		for(let stage = 1; stage <= NUMBER_OF_STAGES; stage++) {
			const answeredInStage = Math.min(Math.max(answered - (stage-1)*stageSize, 0), stageSize);
			let fill = 100 * answeredInStage / stageSize;
			/* The first stage carries the head start, so it is never wholly empty */
			if(stage === 1) fill = Math.max(fill, 100 * SETUP_CREDIT * NUMBER_OF_STAGES);
			stages.push(
				<div className="stage" key={stage.toString()}>
					<div className="stage-fill" style={{width: Math.min(fill, 100) + '%'}}></div>
				</div>
			);
		}

		return (
			<div className="progress-block">
				<p className="progress-caption">
					<em>{minutesLeft(numberOfQuestions, maxNumberOfQuestions, secondsPerQuestion)}</em>
					<span className="progress-stage">Stage {currentStage} of {NUMBER_OF_STAGES}</span>
				</p>
				<div className="stages" aria-valuenow={progressInPercents} aria-valuemin="0" aria-valuemax="100" role="progressbar">
					{stages}
				</div>
			</div>
		);
	}
}


export default ProgressBar
